import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Phone, Clock, Target, Search, ArrowUpDown } from "lucide-react";
import Select from "react-select";
import Card from "src/components/Card";
import ReactSelect from "src/base-components/ReactSelect";
import DatePicker from "src/base-components/DatePicker";
import type { DateValueType } from "react-tailwindcss-datepicker";
import CallLogsTable from "./CallLogsTable";
import { CallLogsState, userProps } from "src/interfaces";
import { RootState, useAppSelector } from "src/stores";
import {
  fetchCallLogs,
  fetchCallStats,
  selectCallStats,
} from "src/stores/calllogs";
import {
  fetchAllUsers,
  fetchLocationBaseAgents,
  fetchUsers,
} from "src/stores/user";
import { selectNotifications } from "src/stores/notification";
import LoadingIcon from "src/base-components/LoadingIcon";
import Button from "src/base-components/Button";
import Lucide from "src/base-components/Lucide";
import ExportCallLogs from "./ExportCallLogs";
import { checkPermission } from "src/helpers";
import { callAPIUrls } from "src/common/apiActions";
import CallLogsTabs from "src/components/CallLogsTabs";

// ✅ Skeleton
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export interface SelectOption {
  label: string;
  value: string;
}

// allow null in FiltersState
export interface FiltersState {
  search: string;
  status: SelectOption | null;
  type: SelectOption | null;
  remark_filter: SelectOption | null;
  direction: SelectOption | null;
  sort: SelectOption | null;
  date: DateValueType;
  orderBy: string;
  agentIds?: string[];
  customer_type?: SelectOption;
}

// Options for ReactSelect components
const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "answered", label: "Answered" },
  { value: "completed", label: "Completed" },
  { value: "missed", label: "Missed" },
  { value: "declined", label: "Declined" },
];
const remarkFilterOptions = [
  { value: "all", label: "All Remarks" },
  { value: "pending", label: "Pending Remarks" },
  { value: "remarks", label: "Filled Remarks" },
];
const typeOptions = [
  { value: "all", label: "All Types" },
  { value: "1", label: "Emergency" },
  { value: "2,4", label: "General" },
  { value: "3", label: "Date Change" },
  { value: "2,4", label: "New Query" },
];

const sortOptions = [
  { value: "date", label: "Date" },
  { value: "duration", label: "Duration" },
  { value: "status", label: "Status" },
];

const callDirection = [
  { value: "", label: "Both Directions" },
  { value: "Outbound", label: "Outbound" },
  { value: "Inbound", label: "Inbound" },
];

// new added
const customerTypeOptions = [
  { value: "customer", label: "Customer Calls" },
  {value: "internal", label: "Internal Calls"},
  { value: "all", label: "All Calls" },
];

/* ---------------------- SKELETON COMPONENTS ---------------------- */

const CallLogsPageSkeleton = () => (
  <div className="space-y-6">
    {/* Debug: Simple skeleton first */}
    <div className="bg-gray-200 h-4 w-32 rounded animate-pulse"></div>

    {/* Header */}
    <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row justify-between mb-3">
      <Skeleton height={20} width={120} />
      <div className="flex gap-5">
        <Skeleton height={38} width={100} borderRadius={8} />
      </div>
      <Skeleton height={40} width={200} borderRadius={8} />
    </div>

    {/* Stats Cards */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div>
              <Skeleton height={16} width={120} />
              <Skeleton height={32} width={80} className="mt-1" />
            </div>
            <Skeleton height={48} width={48} borderRadius={12} />
          </div>
        </div>
      ))}
    </div>

    {/* Filters */}
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
      <div className="flex flex-wrap gap-4 items-start">
        <Skeleton height={38} width={200} borderRadius={8} />
        <Skeleton height={38} width={200} borderRadius={8} />
        <Skeleton height={38} width={150} borderRadius={8} />
        <Skeleton height={38} width={150} borderRadius={8} />
        <Skeleton height={38} width={150} borderRadius={8} />
        <Skeleton height={38} width={195} borderRadius={8} />
        <Skeleton height={38} width={38} borderRadius={8} />
        <Skeleton height={38} width={60} borderRadius={8} />
      </div>
    </div>

    {/* Table */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      {/* Table Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="grid grid-cols-7 gap-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} height={16} width={80} />
          ))}
        </div>
      </div>

      {/* Table Rows */}
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="p-4 border-b border-gray-100">
          <div className="grid grid-cols-7 gap-4 items-center">
            <Skeleton height={16} width={120} />
            <Skeleton height={16} width={100} />
            <Skeleton height={16} width={80} />
            <Skeleton height={20} width={70} borderRadius={12} />
            <Skeleton height={16} width={60} />
            <Skeleton height={16} width={90} />
            <Skeleton height={32} width={80} borderRadius={8} />
          </div>
        </div>
      ))}
    </div>

    {/* Pagination */}
    <div className="flex justify-between items-center">
      <Skeleton height={16} width={120} />
      <div className="flex gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height={32} width={32} borderRadius={8} />
        ))}
      </div>
    </div>
  </div>
);

const CallLogs = ({
  getCalls,
  users,
  fetchCallLogs,
  fetchData,
  fetchCallStat,
}: {
  getCalls: CallLogsState;
  users: userProps;
  fetchCallLogs: (payload?: any) => void;
  fetchData: (payload?: any) => void;
  fetchCallStat: Function;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedAgentList, setSelectedAgentList] = useState<string[]>([]);
  const [exportVisible, setExportVisible] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  const initialFilters: FiltersState = {
    search: "",
    status: { label: "All Status", value: "all" },
    type: { label: "All Types", value: "all" },
    sort: { label: "Date", value: "date" },
    direction: { label: "Both Directions", value: "" },
    remark_filter: { label: "All", value: "all" },
    date: { startDate: null, endDate: null },
    orderBy: "desc",
    agentIds: [],
    customer_type: { label: "Customer Call", value: "customer" },
  };

  const [filters, setFilters] = useState<FiltersState>(initialFilters);

  const { loading, data } = useAppSelector(selectCallStats);

  const updateFilter = <K extends keyof FiltersState>(
    name: K,
    value: FiltersState[K],
  ) => {

    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const initializeData = async () => {
      if (hasInitialized) return;

      setIsInitialLoad(true);

      // Ensure skeleton shows for at least 800ms
      const minLoadTime = new Promise((resolve) => setTimeout(resolve, 800));

      try {
        await Promise.all([fetchData(), fetchCallStat(), minLoadTime]);
      } catch (error) {
        console.error("Failed to initialize data:", error);
      } finally {
        setIsInitialLoad(false);
        setHasInitialized(true);
      }
    };

    initializeData();
  }, [hasInitialized]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.search,
    filters.status?.value,
    filters.type?.value,
    filters.sort?.value,
    filters.date?.startDate,
    filters.direction?.value,
    filters.date?.endDate,
    filters.orderBy,
    filters.remark_filter?.value,
    filters.customer_type?.value,
    JSON.stringify(filters.agentIds || []),
  ]);

  // Reset to page 1 when pageSize changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  // Fetch data when page or filters change
  useEffect(() => {
    if (!hasInitialized) return; // Don't fetch until initial load is complete

    const timer = setTimeout(async () => {
      setIsTableLoading(true);

      // Ensure table skeleton shows for at least 300ms
      const minLoadTime = new Promise((resolve) => setTimeout(resolve, 300));

      try {
        const payload = {
          current_page: currentPage,
          per_page: pageSize,
          search: filters.search ?? "",
          status: filters.status?.value ?? "all",
          type: filters.type?.value ?? "all",
          direction: filters.direction?.value ?? "",
          sort_by: filters.sort?.value ?? "date",
          sort_order: filters.orderBy ?? "desc",
          startDate: filters.date?.startDate ?? null,
          endDate: filters.date?.endDate ?? null,
          agent_ids: filters.agentIds?.length ? filters.agentIds : [],
          remark_filter: filters.remark_filter?.value ?? "all",
          customer_type: filters.customer_type?.value ?? "customer"
        };

        await Promise.all([fetchCallLogs(payload), minLoadTime]);
      } catch (error) {
        console.error("Failed to fetch call logs:", error);
      } finally {
        setIsTableLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [
    hasInitialized,
    currentPage,
    filters.search,
    filters.status?.value,
    filters.type?.value,
    filters.sort?.value,
    filters.date?.startDate,
    filters.direction?.value,
    filters.date?.endDate,
    filters.orderBy,
    filters.remark_filter?.value,
    filters.customer_type?.value,
    pageSize,
    JSON.stringify(filters.agentIds || []),
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Answered":
        return "bg-green-100 text-green-800 border-green-200";
      case "Completed":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Missed":
        return "bg-red-100 text-red-800 border-red-200";
      case "Declined":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleRowClick = (
    callId: string | number,
    session_id?: string | null,
  ) => {
    navigate(`/call-logs/${session_id}`, { state: { session_id } });
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setSelectedAgentList([]);
    setIsTableLoading(true); // Show loading when clearing filters
  };

  const canEnableOrderBy = (filters: FiltersState) => {
    const hasSearch = filters.search.trim() !== "";
    const hasStatus = filters.status?.value !== "all";
    const hasType = filters.type?.value !== "all";
    const hasSort = filters.sort?.value !== "all";
    const hasDate =
      filters.date?.startDate !== null || filters.date?.endDate !== null;
    const hasAgents = filters.agentIds && filters.agentIds.length > 0;

    return hasSearch || hasStatus || hasType || hasSort || hasDate || hasAgents;
  };

  const handleOrderBy = () => {
    if (!canEnableOrderBy(filters)) {
      return;
    }

    setFilters((prev) => ({
      ...prev,
      orderBy: prev.orderBy === "asc" ? "desc" : "asc",
    }));
  };

  const stats = [
    {
      title: "Total Calls Today",
      value: data?.total_calls_today,
      icon: Phone,
      bg: "bg-blue-50",
      color: "text-blue-600",
    },
    {
      title: "Avg Call Duration",
      value: data?.avg_call_duration,
      icon: Clock,
      bg: "bg-green-50",
      color: "text-green-600",
    },
    {
      title: "Answer Rate",
      value: data?.answer_rate,
      icon: Target,
      bg: "bg-purple-50",
      color: "text-purple-600",
    },
  ];

  // Prefer agentManager (managers) -> agents (location-based) -> data (legacy)
  const usersList = Array.isArray(users?.agentManager)
    ? users.agentManager
    : Array.isArray(users?.agents)
      ? users.agents
      : Array.isArray(users?.data)
        ? users.data
        : [];

  const agentOptions = usersList.map((u: any) => {
    const name = `${u?.name ?? ""} ${u?.last_name ?? ""}`.trim();
    const ext = u?.agent_extension?.extension_number || u?.extension_number;

    return {
      label: ext ? `${name} (${ext})` : name || "Unknown User",
      value: u?.id?.toString(),
      raw: u,
    };
  });

  const exportDataList = usersList.map((u: any) => ({
    label:
      u?.name || u?.last_name
        ? `${u?.name ?? ""} ${u?.last_name ?? ""}`.trim()
        : "Unknown User",
    val: u?.id,
  }));
  // Show skeleton during initial load
  if (isInitialLoad) {

    return (
      <div className="p-6 space-y-6">
        <CallLogsPageSkeleton />
      </div>
    );
  }
  return (
    <div className="">
      {/* Header */}
      <div className="">
        {/* Stats Cards */}
        <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row justify-between mb-3">
          <div className="text-base font-medium">Call Logs</div>

          <div className="flex justify-center   gap-8 items-center">
            {checkPermission(callAPIUrls?.getExportCallLogs) ? (
              <Button
                variant="primary"
                onClick={() => setExportVisible(true)}
                className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent"
              >
                <Lucide icon="FileCheck2" className="w-4 h-4 mr-2" />
                Export
              </Button>
            ) : null}

            {checkPermission(callAPIUrls?.getCallLogsAnalytics) ? (
              <CallLogsTabs className="" />
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((item, index) => (
            <Card
              key={index}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            >
              {loading ? (
                <div className="w-7 h-7">
                  <LoadingIcon />
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {item.title}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">
                      {item.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${item.bg}`}>
                    <item.icon className={`w-6 h-6 ${item.color}`} />
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* Filters and Search */}
      <Card className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
        <div className="flex flex-wrap gap-4 items-start">
          {/* Search */}
          <div className="relative flex-1 w-full min-w-[200px] max-w-[200px]">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={filters.search}
              onChange={(e) => {
                if (/^\s/.test(e.target.value)) return;
                updateFilter("search", e.target.value);
              }}
              className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Date Range */}
          <div className="relative min-w-[200px]">
            <DatePicker
              value={filters.date}
              onChange={(value) => updateFilter("date", value)}
              classNames={{
                input: () =>
                  "max-h-[38px] transition duration-200 ease-in-out w-full text-sm border-slate-300/100 shadow-sm !rounded-md placeholder:text-slate-400/90 focus:ring-primary focus:ring-opacity-20 focus:border-none focus:ring-4",
              }}
              placeholder="Select date range"
              separator="-"
              showFooter={true}
            />
          </div>

          {/* Type Filter */}
          <div className="relative min-w-[150px]">
            <ReactSelect
              name="type"
              placeholder="All Types"
              value={filters.type?.value ?? null}
              onChange={(_, value) => {
                const option =
                  value && typeof value === "string"
                    ? { label: value, value }
                    : null;
                updateFilter("type", option);
              }}
              options={typeOptions}
            />
          </div>

          {/* Direction Filter */}
          <div className="relative min-w-[150px]">
            <ReactSelect
              name="direction"
              placeholder="Both Directions"
              value={filters.direction?.value ?? null}
              onChange={(_, value) => {
                const option =
                  value && typeof value === "string"
                    ? { label: value, value }
                    : null;
                updateFilter("direction", option);
              }}
              options={callDirection}
            />
          </div>

          <div className="relative min-w-[150px]">
            <ReactSelect
              name="remark_filter"
              placeholder="All"
              value={filters.remark_filter?.value ?? null}
              onChange={(_, value) => {
                const option =
                  value && typeof value === "string"
                    ? (remarkFilterOptions.find((o) => o.value === value) ?? {
                      label: value,
                      value,
                    })
                    : null;
                updateFilter("remark_filter", option);
              }}
              options={remarkFilterOptions}
            />
          </div>

          {/* Agent Filter */}
          {/* {checkPermission(callAPIUrls?.getAllUsers) && (
        
          )} */}

          <div className="relative min-w-[195px] max-w-[195px]">
            <Select
              isMulti
              options={agentOptions}
              value={agentOptions.filter((opt) =>
                selectedAgentList.includes(opt.value),
              )}
              onMenuOpen={() => {
                console.log("Agent select opened, usersList:", usersList);
                if (!usersList || usersList.length === 0) {
                  // Try location-based agents first
                  fetchData();
                }
              }}
              noOptionsMessage={() => "No agents loaded — open to fetch"}
              onChange={(selected) => {
                const arr = Array.isArray(selected) ? selected : [];
                const ids = arr.map((o) => o.value.toString());
                setSelectedAgentList(ids);
                updateFilter("agentIds", ids);
              }}
              placeholder="All Agents"
              className="min-w-36 rounded-2xl"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  boxShadow: state.isFocused ? "0 0 0 1px #cbd5e1" : "none",
                  borderRadius: "0.375rem",
                  overflow: "hidden",
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  padding: "4px 8px",
                  gap: "6px",
                  maxHeight: "60px",
                  overflowY: "auto",
                }),
              }}
            />
          </div>

          {/* Added new dropdown according to customer */}

        { checkPermission(callAPIUrls?.getCallLogs) &&  <div className="relative min-w-[150px]">
            <ReactSelect
              name="customer_type"
              placeholder="Customer Call"
              value={filters.customer_type?.value ?? "customer"}
              onChange={(_, value) => {
                const option =
                  value && typeof value === "string"
                    ? (customerTypeOptions.find((o) => o.value === value) ?? {
                      label: value,
                      value,
                    })
                    : undefined; // ✅ was null, changed to undefined
                updateFilter("customer_type", option);
              }}
              options={customerTypeOptions}
            />
          </div>
}
          {/* Order By Button */}
          <button
            onClick={handleOrderBy}
            disabled={!canEnableOrderBy(filters)}
            className={`p-2.5 border rounded-lg transition-colors
              ${!canEnableOrderBy(filters)
                ? "opacity-40 cursor-not-allowed border-gray-400"
                : "border-gray-400 hover:bg-gray-50 hover:border-gray-500"
              }
            `}
            title={`Sort order: ${filters.orderBy.toUpperCase()}`}
          >
            <ArrowUpDown
              className={`w-4 h-4 transition-transform ${filters.orderBy === "asc" ? "rotate-180" : ""
                } ${!canEnableOrderBy(filters) ? "text-gray-400" : "text-gray-600"
                }`}
            />
          </button>

          {/* Clear Button */}
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Clear
          </button>
        </div>
      </Card>
      <ExportCallLogs
        visible={exportVisible}
        setVisible={setExportVisible}
        dataList={exportDataList}
        filters={filters}
        currentPage={currentPage}
        pageSize={pageSize}
      />

      {/* Call Logs Table */}
      <div className="mt-12">
        {isTableLoading ? (
          <div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              {/* Table Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="grid grid-cols-7 gap-4">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <Skeleton key={i} height={16} width={80} />
                  ))}
                </div>
              </div>

              {/* Table Rows */}
              {Array.from({ length: pageSize }).map((_, i) => (
                <div key={i} className="p-4 border-b border-gray-100">
                  <div className="grid grid-cols-7  gap-4 items-center">
                    <Skeleton height={16} width={120} />
                    <Skeleton height={16} width={100} />
                    <Skeleton height={16} width={80} />
                    <Skeleton height={20} width={70} borderRadius={12} />
                    <Skeleton height={16} width={60} />
                    <Skeleton height={16} width={90} />
                    <Skeleton height={32} width={80} borderRadius={8} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <CallLogsTable
            callLogs={getCalls}
            handleRowClick={handleRowClick}
            getStatusColor={getStatusColor}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        )}
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  getCalls: state.callLogs,
  users: state.users,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchData: (payload?: any) => dispatch(fetchLocationBaseAgents(payload)),
  fetchCallLogs: (payload: any) => dispatch(fetchCallLogs(payload)),
  fetchCallStat: () => dispatch(fetchCallStats()),
  fetchAllUsers: () => dispatch(fetchAllUsers()),
});

export default connect(mapState, mapDispatchToProps)(CallLogs);
